import { Plus, Trash2, Loader2, Wand2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "./Textarea";
import {
	RUNNING_TYPES,
	getRunningTargetPaceMode,
	getRunningBenchmarkPaceValue,
	resolveRunningReferencedPace,
	getRunningPaceModifierHint,
	getFartlekSegmentPaceMode,
	normalizeFartlekSegmentType,
	nextEnduranceName,
} from "../../lib/programBuilderUtils";
import { EnduranceNameField } from "./EnduranceNameField";

export function RunningForm({
	newExercise,
	setNewExercise,
	benchmarkOptions,
	aiEnduranceParsing,
	handleParseEnduranceCustom,
	t,
}) {
	return (
		<div className="space-y-3">
			{/* Running type selector */}
			<div className="space-y-2">
				<Label className="label-tactical">{t("pbRunTypeLabel")}</Label>
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
					{RUNNING_TYPES.map((rt) => (
						<button
							key={rt.value}
							onClick={() => {
								const updates = {
									...newExercise,
									running_type: rt.value,
									exercise_name: nextEnduranceName(
										newExercise.exercise_name,
										`Running - ${rt.label}`,
									),
								};
								if (
									["ripetute", "salite"].includes(rt.value) &&
									(!newExercise.interval_rows ||
										newExercise.interval_rows.length === 0)
								) {
									updates.interval_rows = [
										{
											repetitions: 1,
											distance: "",
											target_pace: "",
											target_pace_mode: "manual",
											target_pace_reference_benchmark_id: null,
											target_pace_reference_benchmark_name: null,
											target_pace_reference_percentage: null,
											target_pace_reference_pace: "",
											target_pace_resolved: "",
											recovery_time: "",
											recovery_type: "jog",
											notes: "",
										},
									];
								}
								if (
									rt.value === "fartlek" &&
									(!newExercise.fartlek_segments ||
										newExercise.fartlek_segments.length === 0)
								) {
									updates.fartlek_segments = [
										{
											segment_type: "fast",
											duration: "",
											pace: "",
											pace_mode: "manual",
											pace_reference_benchmark_id: null,
											pace_reference_benchmark_name: null,
											pace_reference_percentage: null,
											pace_reference_pace: "",
											pace_resolved: "",
											notes: "",
										},
										{
											segment_type: "medium",
											duration: "",
											pace: "",
											pace_mode: "manual",
											pace_reference_benchmark_id: null,
											pace_reference_benchmark_name: null,
											pace_reference_percentage: null,
											pace_reference_pace: "",
											pace_resolved: "",
											notes: "",
										},
										{
											segment_type: "slow",
											duration: "",
											pace: "",
											pace_mode: "manual",
											pace_reference_benchmark_id: null,
											pace_reference_benchmark_name: null,
											pace_reference_percentage: null,
											pace_reference_pace: "",
											pace_resolved: "",
											notes: "",
										},
									];
								}
								setNewExercise(updates);
							}}
							className={`p-2 rounded-sm border text-xs text-center transition-colors ${
								newExercise.running_type === rt.value
									? "border-green-400 bg-green-400/10 text-green-400"
									: "border-border text-muted-foreground hover:border-green-400/50"
							}`}
						>
							{t(rt.labelKey)}
						</button>
					))}
				</div>
			</div>

			{newExercise.running_type && (
				<EnduranceNameField
					newExercise={newExercise}
					setNewExercise={setNewExercise}
					t={t}
				/>
			)}

			{/* Warmup — for all running types except custom */}
			{newExercise.running_type &&
				newExercise.running_type !== "custom" &&
				newExercise.running_type !== "benchmark" && (
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("warmup")}
						</span>
						<Input
							value={newExercise.warmup_description ?? ""}
							onChange={(e) =>
								setNewExercise({
									...newExercise,
									warmup_description: e.target.value,
								})
							}
							className="bg-secondary border-transparent rounded-sm text-xs"
							placeholder={t("pbRunWarmupPlaceholder")}
						/>
					</div>
				)}

			{/* === BENCHMARK EXERCISE PICKER (Running) === */}
			{newExercise.running_type === "benchmark" && (
				<div className="space-y-3">
					<div className="space-y-1">
						<Label className="label-tactical">{t("pbSelectBenchmark")}</Label>
						{benchmarkOptions.running.length > 0 ? (
							<div className="grid grid-cols-2 gap-2">
								{benchmarkOptions.running.map((bm) => (
									<button
										key={bm.exercise_id}
										onClick={() =>
											setNewExercise({
												...newExercise,
												benchmark_exercise_id: bm.exercise_id,
												benchmark_exercise_name: bm.name,
												exercise_name: `Running Benchmark - ${bm.name}`,
											})
										}
										className={`p-2 rounded-sm border text-xs text-left transition-colors ${
											newExercise.benchmark_exercise_id === bm.exercise_id
												? "border-green-400 bg-green-400/10 text-green-400"
												: "border-border text-muted-foreground hover:border-green-400/50"
										}`}
									>
										{bm.name}
									</button>
								))}
							</div>
						) : (
							<p className="text-xs text-muted-foreground">
								{t("pbNoBenchmarkRunning")}
							</p>
						)}
					</div>
				</div>
			)}

			{/* === RIPETUTE / SALITE: Multi-row intervals === */}
			{["ripetute", "salite"].includes(newExercise.running_type) && (
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{newExercise.running_type === "salite"
								? t("pbHillRepeats")
								: t("pbIntervalSeries")}
						</span>
						<Button
							variant="ghost"
							size="sm"
							onClick={() =>
								setNewExercise({
									...newExercise,
									interval_rows: [
										...(newExercise.interval_rows || []),
										{
											repetitions: 1,
											distance: "",
											target_pace: "",
											target_pace_mode: "manual",
											target_pace_reference_benchmark_id: null,
											target_pace_reference_benchmark_name: null,
											target_pace_reference_percentage: null,
											target_pace_reference_pace: "",
											target_pace_resolved: "",
											recovery_time: "",
											recovery_type: "jog",
											notes: "",
										},
									],
								})
							}
							className="h-6 px-2 text-green-400 hover:text-green-300"
						>
							<Plus className="w-3 h-3 mr-1" /> {t("sets")}
						</Button>
					</div>
					{(newExercise.interval_rows || []).map((row, ri) => (
						<div key={ri} className="bg-secondary/50 rounded-sm p-3 space-y-2">
							<div className="flex items-center justify-between">
								<span className="text-xs font-medium text-green-400">
									Serie {ri + 1}
								</span>
								<button
									onClick={() => {
										const rows = [...(newExercise.interval_rows || [])];
										rows.splice(ri, 1);
										setNewExercise({
											...newExercise,
											interval_rows: rows,
										});
									}}
									className="text-muted-foreground hover:text-destructive"
								>
									<Trash2 className="w-3 h-3" />
								</button>
							</div>
							<div className="grid grid-cols-2 gap-2">
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("reps")}
									</span>
									<Input
										type="text"
										inputMode="numeric"
										value={row.repetitions ?? ""}
										onFocus={(e) => e.target.select()}
										onChange={(e) => {
											const rows = [...(newExercise.interval_rows || [])];
											rows[ri] = {
												...rows[ri],
												repetitions:
													e.target.value === ""
														? ""
														: Number(e.target.value),
											};
											setNewExercise({
												...newExercise,
												interval_rows: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="4"
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("distanceLabel")}
									</span>
									<Input
										value={row.distance ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.interval_rows || [])];
											rows[ri] = {
												...rows[ri],
												distance: e.target.value,
											};
											setNewExercise({
												...newExercise,
												interval_rows: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="400m"
									/>
								</div>
							</div>
							<div className="grid grid-cols-3 gap-2">
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbPaceTarget")}
									</span>
									{(() => {
										const rowPaceMode = getRunningTargetPaceMode(row);
										return (
											<div className="space-y-1">
												<div className="flex gap-1">
													<button
														type="button"
														onClick={() => {
															const rows = [
																...(newExercise.interval_rows || []),
															];
															rows[ri] = {
																...rows[ri],
																target_pace_mode: "manual",
																target_pace_resolved: "",
															};
															setNewExercise({
																...newExercise,
																interval_rows: rows,
															});
														}}
														className={`flex-1 px-2 py-1 rounded-sm border text-[10px] ${
															rowPaceMode === "manual"
																? "border-green-400 bg-green-400/10 text-green-400"
																: "border-border text-muted-foreground"
														}`}
													>
														{t("manualLabel")}
													</button>
													<button
														type="button"
														onClick={() => {
															const rows = [
																...(newExercise.interval_rows || []),
															];
															rows[ri] = {
																...rows[ri],
																target_pace: "",
																target_pace_mode: "reference",
															};
															setNewExercise({
																...newExercise,
																interval_rows: rows,
															});
														}}
														className={`flex-1 px-2 py-1 rounded-sm border text-[10px] ${
															rowPaceMode === "reference"
																? "border-green-400 bg-green-400/10 text-green-400"
																: "border-border text-muted-foreground"
														}`}
													>
														{t("pbDockReference")}
													</button>
												</div>

												{rowPaceMode === "manual" ? (
													<Input
														value={row.target_pace ?? ""}
														onChange={(e) => {
															const rows = [
																...(newExercise.interval_rows || []),
															];
															rows[ri] = {
																...rows[ri],
																target_pace: e.target.value,
																target_pace_resolved: "",
															};
															setNewExercise({
																...newExercise,
																interval_rows: rows,
															});
														}}
														className="bg-secondary border-transparent rounded-sm text-xs"
														placeholder="3:30/km"
													/>
												) : (
													<div className="space-y-1">
														<select
															value={
																row.target_pace_reference_benchmark_id || ""
															}
															onChange={(e) => {
																const bm = benchmarkOptions.running.find(
																	(b) => b.exercise_id === e.target.value,
																);
																const rows = [
																	...(newExercise.interval_rows || []),
																];
																rows[ri] = {
																	...rows[ri],
																	target_pace_reference_benchmark_id:
																		e.target.value || null,
																	target_pace_reference_benchmark_name:
																		bm?.name || null,
																	target_pace_reference_pace: e.target.value
																		? getRunningBenchmarkPaceValue(
																				bm,
																				rows[ri].target_pace_reference_pace,
																			)
																		: "",
																};
																rows[ri].target_pace_resolved =
																	resolveRunningReferencedPace(
																		rows[ri].target_pace_reference_pace,
																		rows[ri].target_pace_reference_percentage,
																	);
																setNewExercise({
																	...newExercise,
																	interval_rows: rows,
																});
															}}
															className="w-full bg-secondary border border-border rounded-sm text-xs p-2"
														>
															<option value="">Benchmark</option>
															{benchmarkOptions.running.map((bm) => (
																<option
																	key={bm.exercise_id}
																	value={bm.exercise_id}
																>
																	{bm.name}
																</option>
															))}
														</select>
														<div className="flex gap-1 items-center">
															<Input
																value={
																	row.target_pace_reference_percentage ?? ""
																}
																onChange={(e) => {
																	const rows = [
																		...(newExercise.interval_rows || []),
																	];
																	rows[ri] = {
																		...rows[ri],
																		target_pace_reference_percentage:
																			e.target.value.trim() || null,
																	};
																	rows[ri].target_pace_resolved =
																		resolveRunningReferencedPace(
																			rows[ri].target_pace_reference_pace,
																			rows[ri]
																				.target_pace_reference_percentage,
																		);
																	setNewExercise({
																		...newExercise,
																		interval_rows: rows,
																	});
																}}
																className="flex-1 bg-secondary border-transparent rounded-sm text-xs"
																placeholder={t("pbBenchmarkModifierPlaceholder")}
																title={t("pbBenchmarkModifier")}
															/>
															{row.target_pace_resolved && (
																<span className="text-[10px] text-green-400 shrink-0 whitespace-nowrap">
																	→ {row.target_pace_resolved}
																</span>
															)}
														</div>
														<p className="text-[10px] text-muted-foreground">
															{getRunningPaceModifierHint(
																row.target_pace_reference_percentage,
																t,
															)}
														</p>
													</div>
												)}
											</div>
										);
									})()}
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("recovery")}
									</span>
									<Input
										value={row.recovery_time ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.interval_rows || [])];
											rows[ri] = {
												...rows[ri],
												recovery_time: e.target.value,
											};
											setNewExercise({
												...newExercise,
												interval_rows: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="90s"
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbRestType")}
									</span>
									<div className="flex gap-1">
										{["jog", "rest"].map((rt) => (
											<button
												key={rt}
												onClick={() => {
													const rows = [
														...(newExercise.interval_rows || []),
													];
													rows[ri] = {
														...rows[ri],
														recovery_type: rt,
													};
													setNewExercise({
														...newExercise,
														interval_rows: rows,
													});
												}}
												className={`flex-1 px-2 py-1 rounded-sm border text-[10px] text-center ${
													row.recovery_type === rt
														? "border-green-400 bg-green-400/10 text-green-400"
														: "border-border text-muted-foreground"
												}`}
											>
												{rt === "jog" ? "Jog" : "Fermo"}
											</button>
										))}
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{/* === FARTLEK: Segment-based === */}
			{newExercise.running_type === "fartlek" && (
				<div className="space-y-2">
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground">
							{t("pbBlockReps")}
						</span>
						<div className="flex items-center gap-2">
							<Input
								type="text"
								inputMode="numeric"
								value={newExercise.fartlek_repeat ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										fartlek_repeat:
											e.target.value === ""
												? null
												: Number(e.target.value),
									})
								}
								className="bg-secondary border-transparent rounded-sm text-xs w-24"
								placeholder="5"
							/>
							<span className="text-[10px] text-muted-foreground">{t("pbTimes")}</span>
						</div>
					</div>
					<div className="flex items-center justify-between">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("pbSegments")}
						</span>
						<Button
							variant="ghost"
							size="sm"
							onClick={() =>
								setNewExercise({
									...newExercise,
									fartlek_segments: [
										...(newExercise.fartlek_segments || []),
										{
											segment_type: "medium",
											duration: "",
											pace: "",
											pace_mode: "manual",
											pace_reference_benchmark_id: null,
											pace_reference_benchmark_name: null,
											pace_reference_percentage: null,
											pace_reference_pace: "",
											pace_resolved: "",
											notes: "",
										},
									],
								})
							}
							className="h-6 px-2 text-green-400 hover:text-green-300"
						>
							<Plus className="w-3 h-3 mr-1" /> {t("pbSegment")}
						</Button>
					</div>
					{(newExercise.fartlek_segments || []).map((seg, si) => {
						const normalizedSegmentType = normalizeFartlekSegmentType(
							seg.segment_type,
						);
						const segmentPaceMode = getFartlekSegmentPaceMode(seg);
						return (
							<div
								key={si}
								className={`rounded-sm p-3 space-y-2 ${
									normalizedSegmentType === "fast"
										? "bg-green-400/5 border border-green-400/20"
										: normalizedSegmentType === "medium"
											? "bg-yellow-400/5 border border-yellow-400/20"
											: "bg-secondary/50"
								}`}
							>
								<div className="flex items-center justify-between">
									<div className="flex gap-1">
										{["fast", "medium", "slow"].map((st) => (
											<button
												key={st}
												onClick={() => {
													const segs = [
														...(newExercise.fartlek_segments || []),
													];
													segs[si] = {
														...segs[si],
														segment_type: st,
													};
													setNewExercise({
														...newExercise,
														fartlek_segments: segs,
													});
												}}
												className={`px-2 py-0.5 rounded-sm text-[10px] border ${
													normalizedSegmentType === st
														? st === "fast"
															? "border-green-400 bg-green-400/10 text-green-400"
															: st === "medium"
																? "border-yellow-400 bg-yellow-400/10 text-yellow-400"
																: "border-blue-400 bg-blue-400/10 text-blue-400"
														: "border-border text-muted-foreground"
												}`}
											>
												{st === "fast"
													? "Veloce"
													: st === "medium"
														? "Medio"
														: "Lento"}
											</button>
										))}
									</div>
									{(newExercise.fartlek_segments || []).length > 1 && (
										<button
											onClick={() => {
												const segs = [
													...(newExercise.fartlek_segments || []),
												];
												segs.splice(si, 1);
												setNewExercise({
													...newExercise,
													fartlek_segments: segs,
												});
											}}
											className="text-muted-foreground hover:text-destructive"
										>
											<Trash2 className="w-3 h-3" />
										</button>
									)}
								</div>
								<div className="grid grid-cols-2 gap-2">
									<div className="space-y-1">
										<span className="text-[10px] text-muted-foreground">
											{t("pbDurationOrDistance")}
										</span>
										<Input
											value={seg.duration ?? ""}
											onChange={(e) => {
												const segs = [
													...(newExercise.fartlek_segments || []),
												];
												segs[si] = {
													...segs[si],
													duration: e.target.value,
												};
												setNewExercise({
													...newExercise,
													fartlek_segments: segs,
												});
											}}
											className="bg-secondary border-transparent rounded-sm text-xs"
											placeholder={
												normalizedSegmentType === "fast"
													? "3:00 o 400m"
													: normalizedSegmentType === "medium"
														? "4:00 o 500m"
														: "2:00 o 200m"
											}
										/>
									</div>
									<div className="space-y-1">
										<span className="text-[10px] text-muted-foreground">{`${t("paceLabel")} / ${t("intensity")}`}</span>
										<div className="mt-1 flex gap-1">
											<button
												type="button"
												onClick={() => {
													const segs = [
														...(newExercise.fartlek_segments || []),
													];
													segs[si] = {
														...segs[si],
														pace_mode: "manual",
													};
													setNewExercise({
														...newExercise,
														fartlek_segments: segs,
													});
												}}
												className={`px-2 py-0.5 rounded-sm text-[10px] border ${
													segmentPaceMode === "manual"
														? "border-green-400 bg-green-400/10 text-green-400"
														: "border-border text-muted-foreground"
												}`}
											>
												{t("paceShort")}
											</button>
											<button
												type="button"
												onClick={() => {
													const segs = [
														...(newExercise.fartlek_segments || []),
													];
													segs[si] = {
														...segs[si],
														pace: "",
														pace_mode: "reference",
													};
													setNewExercise({
														...newExercise,
														fartlek_segments: segs,
													});
												}}
												className={`px-2 py-0.5 rounded-sm text-[10px] border ${
													segmentPaceMode === "reference"
														? "border-green-400 bg-green-400/10 text-green-400"
														: "border-border text-muted-foreground"
												}`}
											>
												{t("pbDockReference")}
											</button>
										</div>
										{segmentPaceMode === "manual" ? (
											<Input
												value={seg.pace ?? ""}
												onChange={(e) => {
													const segs = [
														...(newExercise.fartlek_segments || []),
													];
													segs[si] = {
														...segs[si],
														pace: e.target.value,
														pace_mode: "manual",
													};
													setNewExercise({
														...newExercise,
														fartlek_segments: segs,
													});
												}}
												className="bg-secondary border-transparent rounded-sm text-xs"
												placeholder={
													normalizedSegmentType === "fast"
														? "4:00/km"
														: normalizedSegmentType === "medium"
															? "4:45/km"
															: "5:30/km"
												}
											/>
										) : benchmarkOptions.running.length > 0 ? (
											<div className="mt-1 border border-dashed border-green-400/30 rounded-sm p-2 space-y-2">
												<span className="text-[10px] text-green-400 uppercase tracking-wider font-semibold">
													{t("pbPaceReference")}
												</span>
												<div className="space-y-1">
													<div className="flex gap-1 items-center">
														<select
															value={
																seg.pace_reference_benchmark_id || ""
															}
															onChange={(e) => {
																const bm = benchmarkOptions.running.find(
																	(b) => b.exercise_id === e.target.value,
																);
																const segs = [
																	...(newExercise.fartlek_segments || []),
																];
																segs[si] = {
																	...segs[si],
																	pace_reference_benchmark_id:
																		e.target.value || null,
																	pace_reference_benchmark_name:
																		bm?.name || null,
																	pace_reference_pace: e.target.value
																		? getRunningBenchmarkPaceValue(
																				bm,
																				segs[si].pace_reference_pace,
																			)
																		: "",
																};
																segs[si].pace_resolved =
																	resolveRunningReferencedPace(
																		segs[si].pace_reference_pace,
																		segs[si].pace_reference_percentage,
																	);
																setNewExercise({
																	...newExercise,
																	fartlek_segments: segs,
																});
															}}
															className="flex-1 min-w-0 bg-secondary border border-border rounded-sm text-xs p-1"
														>
															<option value="">{t("noneOption")}</option>
															{benchmarkOptions.running.map((bm) => (
																<option
																	key={bm.exercise_id}
																	value={bm.exercise_id}
																>
																	{bm.name}
																</option>
															))}
														</select>
														<Input
															value={
																seg.pace_reference_percentage ?? ""
															}
															onChange={(e) => {
																const segs = [
																	...(newExercise.fartlek_segments || []),
																];
																segs[si] = {
																	...segs[si],
																	pace_reference_percentage:
																		e.target.value.trim() || null,
																};
																segs[si].pace_resolved =
																	resolveRunningReferencedPace(
																		segs[si].pace_reference_pace,
																		segs[si].pace_reference_percentage,
																	);
																setNewExercise({
																	...newExercise,
																	fartlek_segments: segs,
																});
															}}
															className="flex-1 bg-secondary border-transparent rounded-sm text-xs"
															placeholder={t("pbBenchmarkModifierPlaceholder")}
															title={t("pbBenchmarkModifier")}
														/>
													</div>
													<p className="text-[10px] text-muted-foreground">
														{getRunningPaceModifierHint(
															seg.pace_reference_percentage,
															t,
														)}
													</p>
													{seg.pace_resolved && (
														<span className="text-[10px] text-green-400 shrink-0 whitespace-nowrap">
															→ {seg.pace_resolved}
														</span>
													)}
												</div>
											</div>
										) : (
											<p className="text-[10px] text-muted-foreground mt-1">
												{t("pbNoBenchmarkRunningShort")}
											</p>
										)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{/* === LUNGO — simple fields === */}
			{["lungo"].includes(newExercise.running_type) && (
				<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
					<div className="space-y-1">
						<span className="text-xs text-muted-foreground">{t("distanceLabel")}</span>
						<Input
							value={newExercise.distance_meters ?? ""}
							onChange={(e) =>
								setNewExercise({
									...newExercise,
									distance_meters:
										e.target.value === "" ? null : e.target.value,
								})
							}
							className="bg-secondary border-transparent rounded-sm"
							placeholder="10km"
						/>
					</div>
					<div className="space-y-1">
						<span className="text-xs text-muted-foreground">{t("durationMin")}</span>
						<Input
							type="text"
							inputMode="numeric"
							value={newExercise.duration_minutes ?? ""}
							onChange={(e) =>
								setNewExercise({
									...newExercise,
									duration_minutes:
										e.target.value === "" ? null : e.target.value,
								})
							}
							className="bg-secondary border-transparent rounded-sm"
							placeholder="50"
						/>
					</div>
					<div className="space-y-1">
						<span className="text-xs text-muted-foreground">{t("pbPaceTarget")}</span>
						{(() => {
							const paceMode = getRunningTargetPaceMode(newExercise);
							return (
								<div className="space-y-1">
									<div className="flex gap-1">
										<button
											type="button"
											onClick={() =>
												setNewExercise({
													...newExercise,
													target_pace_mode: "manual",
													target_pace_resolved: "",
												})
											}
											className={`flex-1 px-2 py-1 rounded-sm border text-[10px] ${
												paceMode === "manual"
													? "border-green-400 bg-green-400/10 text-green-400"
													: "border-border text-muted-foreground"
											}`}
										>
											{t("manualLabel")}
										</button>
										<button
											type="button"
											onClick={() =>
												setNewExercise({
													...newExercise,
													target_pace: "",
													target_pace_mode: "reference",
												})
											}
											className={`flex-1 px-2 py-1 rounded-sm border text-[10px] ${
												paceMode === "reference"
													? "border-green-400 bg-green-400/10 text-green-400"
													: "border-border text-muted-foreground"
											}`}
										>
											{t("pbDockReference")}
										</button>
									</div>
									{paceMode === "manual" ? (
										<Input
											value={newExercise.target_pace ?? ""}
											onChange={(e) =>
												setNewExercise({
													...newExercise,
													target_pace: e.target.value,
													target_pace_resolved: "",
												})
											}
											className="bg-secondary border-transparent rounded-sm"
											placeholder="5:30/km"
										/>
									) : (
										<div className="space-y-1">
											<select
												value={
													newExercise.target_pace_reference_benchmark_id ||
													""
												}
												onChange={(e) => {
													const bm = benchmarkOptions.running.find(
														(b) => b.exercise_id === e.target.value,
													);
													setNewExercise({
														...newExercise,
														target_pace_reference_benchmark_id:
															e.target.value || null,
														target_pace_reference_benchmark_name:
															bm?.name || null,
														target_pace_reference_pace: e.target.value
															? getRunningBenchmarkPaceValue(
																	bm,
																	newExercise.target_pace_reference_pace,
																)
															: "",
														target_pace_resolved:
															resolveRunningReferencedPace(
																e.target.value
																	? getRunningBenchmarkPaceValue(
																			bm,
																			newExercise.target_pace_reference_pace,
																		)
																	: "",
																newExercise.target_pace_reference_percentage,
															),
													});
												}}
												className="w-full bg-secondary border border-border rounded-sm text-xs p-2"
											>
												<option value="">Benchmark</option>
												{benchmarkOptions.running.map((bm) => (
													<option
														key={bm.exercise_id}
														value={bm.exercise_id}
													>
														{bm.name}
													</option>
												))}
											</select>
											<div className="flex gap-1 items-center">
												<Input
													value={
														newExercise.target_pace_reference_percentage ??
														""
													}
													onChange={(e) =>
														setNewExercise({
															...newExercise,
															target_pace_reference_percentage:
																e.target.value.trim() || null,
															target_pace_resolved:
																resolveRunningReferencedPace(
																	newExercise.target_pace_reference_pace,
																	e.target.value.trim() || null,
																),
														})
													}
													className="flex-1 bg-secondary border-transparent rounded-sm text-xs"
													placeholder={t("pbBenchmarkModifierPlaceholder")}
													title={t("pbBenchmarkModifier")}
												/>
												{newExercise.target_pace_resolved && (
													<span className="text-[10px] text-green-400 shrink-0 whitespace-nowrap">
														→ {newExercise.target_pace_resolved}
													</span>
												)}
											</div>
											<p className="text-[10px] text-muted-foreground">
												{getRunningPaceModifierHint(
													newExercise.target_pace_reference_percentage,
													t,
												)}
											</p>
										</div>
									)}
								</div>
							);
						})()}
					</div>
				</div>
			)}

			{/* === PROGRESSIVO — multi-segment paces === */}
			{newExercise.running_type === "progressivo" && (
				<div className="space-y-2">
					<div className="grid grid-cols-3 gap-3">
						<div className="space-y-1">
							<span className="text-xs text-muted-foreground">
								{t("pbTotalDistance")}
							</span>
							<Input
								value={newExercise.distance_meters ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										distance_meters:
											e.target.value === "" ? null : e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="12km"
							/>
						</div>
						<div className="space-y-1">
							<span className="text-xs text-muted-foreground">
								{t("durationMin")}
							</span>
							<Input
								type="text"
								inputMode="numeric"
								value={newExercise.duration_minutes ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										duration_minutes:
											e.target.value === "" ? null : e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="55"
							/>
						</div>
					</div>
					<div className="flex items-center justify-between">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("pbProgressiveSegments")}
						</span>
						<Button
							variant="ghost"
							size="sm"
							onClick={() =>
								setNewExercise({
									...newExercise,
									fartlek_segments: [
										...(newExercise.fartlek_segments || []),
										{
											segment_type: "work",
											duration: "",
											pace: "",
											pace_mode: "manual",
											pace_reference_benchmark_id: null,
											pace_reference_benchmark_name: null,
											pace_reference_percentage: null,
											pace_reference_pace: "",
											pace_resolved: "",
											notes: "",
										},
									],
								})
							}
							className="h-6 px-2 text-green-400 hover:text-green-300"
						>
							<Plus className="w-3 h-3 mr-1" /> {t("pbSegment")}
						</Button>
					</div>
					{(newExercise.fartlek_segments || []).map((seg, si) => (
						<div
							key={si}
							className="bg-secondary/50 rounded-sm p-2 flex items-center gap-2"
						>
							<span className="text-[10px] text-muted-foreground w-6">
								{si + 1}.
							</span>
							<Input
								value={seg.duration ?? ""}
								onChange={(e) => {
									const segs = [...(newExercise.fartlek_segments || [])];
									segs[si] = {
										...segs[si],
										duration: e.target.value,
									};
									setNewExercise({
										...newExercise,
										fartlek_segments: segs,
									});
								}}
								className="bg-secondary border-transparent rounded-sm text-xs flex-1"
								placeholder="3km"
							/>
							<div className="flex-1 space-y-1">
								<div className="flex gap-1">
									<button
										type="button"
										onClick={() => {
											const segs = [
												...(newExercise.fartlek_segments || []),
											];
											segs[si] = {
												...segs[si],
												pace_mode: "manual",
												pace_resolved: "",
											};
											setNewExercise({
												...newExercise,
												fartlek_segments: segs,
											});
										}}
										className={`flex-1 px-2 py-1 rounded-sm border text-[10px] ${
											(seg.pace_mode || "manual") === "manual"
												? "border-green-400 bg-green-400/10 text-green-400"
												: "border-border text-muted-foreground"
										}`}
									>
										{t("manualLabel")}
									</button>
									<button
										type="button"
										onClick={() => {
											const segs = [
												...(newExercise.fartlek_segments || []),
											];
											segs[si] = {
												...segs[si],
												pace: "",
												pace_mode: "reference",
											};
											setNewExercise({
												...newExercise,
												fartlek_segments: segs,
											});
										}}
										className={`flex-1 px-2 py-1 rounded-sm border text-[10px] ${
											(seg.pace_mode || "manual") === "reference"
												? "border-green-400 bg-green-400/10 text-green-400"
												: "border-border text-muted-foreground"
										}`}
									>
										{t("pbDockReference")}
									</button>
								</div>
								{(seg.pace_mode || "manual") === "manual" ? (
									<Input
										value={seg.pace ?? ""}
										onChange={(e) => {
											const segs = [
												...(newExercise.fartlek_segments || []),
											];
											segs[si] = {
												...segs[si],
												pace: e.target.value,
												pace_resolved: "",
											};
											setNewExercise({
												...newExercise,
												fartlek_segments: segs,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder={`${5.3 - si * 0.3 > 3.5 ? (5.3 - si * 0.3).toFixed(0) : "3:30"}:${si % 2 === 0 ? "00" : "30"}/km`}
									/>
								) : (
									<div className="space-y-1">
										<div className="flex gap-1 items-center">
											<select
												value={seg.pace_reference_benchmark_id || ""}
												onChange={(e) => {
													const bm = benchmarkOptions.running.find(
														(b) => b.exercise_id === e.target.value,
													);
													const segs = [
														...(newExercise.fartlek_segments || []),
													];
													segs[si] = {
														...segs[si],
														pace_reference_benchmark_id:
															e.target.value || null,
														pace_reference_benchmark_name:
															bm?.name || null,
														pace_reference_pace: e.target.value
															? getRunningBenchmarkPaceValue(
																	bm,
																	segs[si].pace_reference_pace,
																)
															: "",
													};
													segs[si].pace_resolved =
														resolveRunningReferencedPace(
															segs[si].pace_reference_pace,
															segs[si].pace_reference_percentage,
														);
													setNewExercise({
														...newExercise,
														fartlek_segments: segs,
													});
												}}
												className="flex-1 min-w-0 bg-secondary border border-border rounded-sm text-xs p-1"
											>
												<option value="">Benchmark</option>
												{benchmarkOptions.running.map((bm) => (
													<option
														key={bm.exercise_id}
														value={bm.exercise_id}
													>
														{bm.name}
													</option>
												))}
											</select>
											<Input
												value={seg.pace_reference_percentage ?? ""}
												onChange={(e) => {
													const segs = [
														...(newExercise.fartlek_segments || []),
													];
													segs[si] = {
														...segs[si],
														pace_reference_percentage:
															e.target.value.trim() || null,
													};
													segs[si].pace_resolved =
														resolveRunningReferencedPace(
															segs[si].pace_reference_pace,
															segs[si].pace_reference_percentage,
														);
													setNewExercise({
														...newExercise,
														fartlek_segments: segs,
													});
												}}
												className="flex-1 bg-secondary border-transparent rounded-sm text-xs"
												placeholder={t("pbBenchmarkModifierPlaceholder")}
												title={t("pbBenchmarkModifier")}
											/>
										</div>
										<p className="text-[10px] text-muted-foreground">
											{getRunningPaceModifierHint(
												seg.pace_reference_percentage,
												t,
											)}
										</p>
									</div>
								)}
							</div>
							{(newExercise.fartlek_segments || []).length > 1 && (
								<button
									onClick={() => {
										const segs = [
											...(newExercise.fartlek_segments || []),
										];
										segs.splice(si, 1);
										setNewExercise({
											...newExercise,
											fartlek_segments: segs,
										});
									}}
									className="text-muted-foreground hover:text-destructive"
								>
									<Trash2 className="w-3 h-3" />
								</button>
							)}
						</div>
					))}
				</div>
			)}

			{/* === CUSTOM — free-text workout === */}
			{newExercise.running_type === "custom" && (
				<div className="space-y-1">
					<div className="flex items-center justify-between">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("workoutDescription")}
						</span>
						<Button
							type="button"
							size="sm"
							variant="outline"
							className="h-7 px-2.5 gap-1.5 text-[10px] border-dashed border-acid/50 text-acid hover:text-acid/80 hover:bg-acid/10 rounded-sm"
							disabled={
								aiEnduranceParsing ||
								!newExercise.custom_workout_text?.trim()
							}
							onClick={() => handleParseEnduranceCustom("running")}
						>
							{aiEnduranceParsing ? (
								<>
									<Loader2 className="w-3 h-3 animate-spin" />
									{t("pbAiParsing")}
								</>
							) : (
								<>
									<Wand2 className="w-3 h-3" />
									{t("pbAiWand")}
								</>
							)}
						</Button>
					</div>
					<Textarea
						value={newExercise.custom_workout_text ?? ""}
						onChange={(e) =>
							setNewExercise({
								...newExercise,
								custom_workout_text: e.target.value,
							})
						}
						className="bg-secondary border-transparent rounded-sm text-xs min-h-[120px] resize-y md:resize overflow-auto"
						placeholder={t("pbRunFreePlaceholder")}
					/>
				</div>
			)}

			{/* Cooldown — for all running types except custom */}
			{newExercise.running_type &&
				newExercise.running_type !== "custom" && (
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("cooldown")}
						</span>
						<Input
							value={newExercise.cooldown_description ?? ""}
							onChange={(e) =>
								setNewExercise({
									...newExercise,
									cooldown_description: e.target.value,
								})
							}
							className="bg-secondary border-transparent rounded-sm text-xs"
							placeholder={t("pbRunCooldownPlaceholder")}
						/>
					</div>
				)}

			{/* Notes — for all running types */}
			{newExercise.running_type && (
				<div className="space-y-1">
					<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("notes")}
					</span>
					<Textarea
						value={newExercise.notes ?? ""}
						onChange={(e) =>
							setNewExercise({
								...newExercise,
								notes: e.target.value,
							})
						}
						className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
						placeholder={t("pbExtraNotesPlaceholder")}
					/>
				</div>
			)}
		</div>
	);
}
